/**
 * 仓内作业监控（包裹停留看板）。
 *
 * 当前为静态页：数据全部来自 mock/，数仓 BI 表就绪后只替换 useBagStayMonitorData 一个文件。
 * 页面结构与交互移植产品门户原型 /bag-stay-monitor：顶部工具条 → 仓内现状（客户筛选 + 六张卡片）→ 流向展开四层树 →
 * 仓内作业（分时折线 + 区间作业量与速率 / 账号作业明细 / 分拣机）→ 明细抽屉。
 *
 * 时间口径与「仓内分析」看板一致：小时级作业数据按 hub 当地时间切分；更新时间、明细里的时间按浏览器时区显示。
 */
import React from 'react';
import { Alert, Button, Empty, Select, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { SiteType } from '../../../../graphql/types/SiteType';
import { C, PILE_BAGS } from './constants';
import type { BagRecord, DetailQuery, DetailStage, StageKey } from './types';
import {
    addInto,
    buildFlowTree,
    emptyCells,
    filterBagsByCustomer,
    filterParcelsByCustomer,
    flowNodes,
    inTransitParcels,
    stageAlerts,
    totalBags,
    type FlowFilter
} from './lib/tree';
import { hasParcelFilter, toDetailQuery, type ParcelFilter } from './lib/detailFilter';
import { fmtDateTimeLocal, hoursSince } from './lib/format';
import { useFmt } from './lib/useFmt';
import { I18N, useLabels } from './lib/useLabels';
import { LOAD_FAILED, useBagStayMonitorData } from './useBagStayMonitorData';
import { MOCK_HUBS, hubZone, mockBatches } from './mock/mockData';
import StageCards from './components/StageCards';
import FlowTable, { type SortKey } from './components/FlowTable';
import OpsSection from './components/OpsSection';
import type { DetailDrawerProps, DrawerState } from './components/DetailDrawer';

const BagStayMonitorPage: React.FC = () => {
    const { t } = useTranslation();
    const fmt = useFmt();
    const L = useLabels();
    const { currentWarehouse } = useWarehouseContext();

    // 与仓内作业看板一致：hub 取 name 的首个 token（如 "LAX Hub" → "LAX"）
    const hubFirstToken = React.useMemo(() => {
        if (!currentWarehouse) return '';
        if (currentWarehouse.siteType === SiteType.HUB) {
            return (currentWarehouse.name ?? '').trim().split(/\s+/)[0];
        }
        return '';
    }, [currentWarehouse]);

    // todo hetianyang mock 只造了 LAX / EWR 两个仓的数据，接真实接口后这段兜底要删掉
    const supported = MOCK_HUBS.includes(hubFirstToken);
    const hub = supported ? hubFirstToken : MOCK_HUBS[0];
    const timeZone = hubZone(hub);

    const { data, loading, error, refresh, fetchDetails, exportDetails } = useBagStayMonitorData(hub);
    const [customer, setCustomer] = React.useState(''); // '' = 全部客户
    const [focusStage, setFocusStage] = React.useState<StageKey | 0>(0);
    const [sort, setSort] = React.useState<SortKey>('total');
    const [drawer, setDrawer] = React.useState<DrawerState | null>(null);
    const drawerToken = React.useRef(0);

    // 停留时长实时计算：每分钟走一次，数据刷新时也同步一次
    const [now, setNow] = React.useState(() => Date.now());
    React.useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 60_000);
        return () => clearInterval(id);
    }, []);
    React.useEffect(() => {
        if (data) setNow(Date.now());
    }, [data]);

    // 换仓后客户列表不同，清掉客户筛选
    React.useEffect(() => {
        setCustomer('');
    }, [hub]);

    const th = data?.overdueThresholdHours ?? 0;

    // 客户筛选：包牌里可能混装多个客户，筛选后只算该客户的包裹数
    const tree = React.useMemo(() => {
        if (!data) return [];
        return buildFlowTree(
            hub,
            filterParcelsByCustomer(data.parcels, customer),
            filterBagsByCustomer(data.bags, customer),
            filterBagsByCustomer(data.stragglerBags, customer),
            now,
            (p, w, n) => mockBatches(p, w, n, timeZone),
            L.tree
        );
    }, [data, hub, customer, now, timeZone, L.tree]);

    const totals = React.useMemo(() => {
        const acc = emptyCells();
        for (const n of tree) addInto(acc, n.cells);
        return acc;
    }, [tree]);

    // 指标卡上的提示：以「流向」（发干线 = 目的地，发支线 = WHS）为单位统计
    const alerts = React.useMemo(() => stageAlerts(flowNodes(tree), now, th, PILE_BAGS), [tree, now, th]);

    const prefix = (scope: string) => [hub, customer ? L.customer(customer) : '', scope].filter(Boolean).join(' · ');

    const openBagDrawer = (scope: string, stage: StageKey, rows: BagRecord[]) => {
        const sorted = [...rows].sort((a, b) => hoursSince(b.stageEnteredAt, now) - hoursSince(a.stageEnteredAt, now));
        drawerToken.current += 1;
        setDrawer({
            kind: 'bags',
            openId: drawerToken.current,
            title: t(`${I18N}.drawer.bagTitle`, { prefix: prefix(scope), stage: L.stageTitle(stage) }) ?? '',
            note: sorted.length ? t(`${I18N}.drawer.bagNote`, { n: fmt.num(sorted.length) }) ?? '' : '',
            rows: sorted,
            emptyHint:
                stage === 0
                    ? t(`${I18N}.drawer.bagEmptyS0`) ?? ''
                    : stage === 1
                    ? t(`${I18N}.drawer.bagEmptyS1`) ?? ''
                    : t(`${I18N}.drawer.bagEmpty`) ?? ''
        });
    };

    // 流向表行末「明细」：该流向 ②~④ 的全部在途包牌
    const openRowBagDrawer = (scope: string, rows: BagRecord[]) => {
        drawerToken.current += 1;
        setDrawer({
            kind: 'bags',
            openId: drawerToken.current,
            title: t(`${I18N}.drawer.rowBagTitle`, { prefix: `${hub} · ${scope}` }) ?? '',
            note: t(`${I18N}.drawer.bagNote`, { n: fmt.num(rows.length) }) ?? '',
            rows: [...rows].sort((a, b) => hoursSince(b.stageEnteredAt, now) - hoursSince(a.stageEnteredAt, now)),
            emptyHint: t(`${I18N}.drawer.rowBagEmpty`) ?? ''
        });
    };

    // 包裹明细：按 q 查一次并填进抽屉。打开抽屉、在抽屉里改筛选都走这里，token 不是最新的说明已被后一次查询取代
    const loadParcels = async (token: number, base: DrawerState, q: DetailQuery, filtered: boolean) => {
        setDrawer({ ...base, pRows: null, pError: undefined, note: t(`${I18N}.drawer.loadingNote`) ?? '' });
        try {
            const rows = await fetchDetails(q);
            if (token !== drawerToken.current) return;
            const vars = {
                total: fmt.num(base.total ?? 0),
                n: fmt.num(rows.length),
                per: fmt.num(data?.detailPerCell ?? 100),
                limit: fmt.num(q.limit ?? rows.length)
            };
            const capped = q.limit !== undefined && rows.length >= q.limit;
            setDrawer({
                ...base,
                pRows: rows,
                pError: undefined,
                note:
                    (filtered
                        ? capped
                            ? t(`${I18N}.drawer.parcelNoteFilteredCapped`, vars)
                            : t(`${I18N}.drawer.parcelNoteFiltered`, vars)
                        : base.stage === 6
                        ? t(`${I18N}.drawer.parcelNoteS6`, vars)
                        : base.stage === 5
                        ? t(`${I18N}.drawer.parcelNoteS5`, vars)
                        : t(`${I18N}.drawer.parcelNote`, vars)) ?? ''
            });
        } catch (e) {
            if (token !== drawerToken.current) return;
            setDrawer({
                ...base,
                pRows: [],
                note: '',
                pError: e instanceof Error && e.message ? e.message : t(`${I18N}.loadError`) ?? ''
            });
        }
    };

    // 包裹明细：每个格子停留最久的前 N 个包裹
    const openParcelDrawer = async (scope: string, stage: DetailStage, filter: FlowFilter, total: number) => {
        const token = ++drawerToken.current;
        const query: DetailQuery = {
            hub,
            stage,
            limit: stage >= 5 ? 5000 : 500,
            customer: customer || undefined,
            branch: filter.br,
            g1: filter.g1 || undefined,
            g2: filter.g1 ? filter.g2 ?? undefined : undefined
        };
        const base: DrawerState = {
            kind: 'parcels',
            openId: token,
            title: t(`${I18N}.drawer.parcelTitle`, { prefix: prefix(scope), stage: L.stageTitle(stage) }) ?? '',
            note: '',
            rows: [],
            stage,
            query,
            total
        };
        await loadParcels(token, base, query, false);
    };

    // 抽屉里改了筛选：在打开时的查询上叠加筛选重新查，标题、格子总数不变
    const onParcelFilter = (f: ParcelFilter) => {
        if (drawer?.kind !== 'parcels' || !drawer.query) return;
        const token = ++drawerToken.current;
        void loadParcels(token, drawer, toDetailQuery(drawer.query, f), hasParcelFilter(f));
    };

    if (!currentWarehouse || currentWarehouse.siteType !== SiteType.HUB) {
        return (
            <div className="rounded-lg bg-white px-3 py-10 shadow-sm">
                <Empty description={t(`${I18N}.noHubSelected`)} />
            </div>
        );
    }

    return (
        <div className="rounded-lg bg-white px-3 pb-3 pt-3 shadow-sm">
            {/* 顶部工具条 */}
            <div className="flex flex-wrap items-center gap-3">
                {data && (
                    <span className="rounded px-2 py-1 text-[12.5px]" style={{ background: C.headBg, color: C.text2 }}>
                        {t(`${I18N}.toolbar.updatedAt`, { time: fmtDateTimeLocal(data.computedAt) })}
                        {data.refreshing && <span style={{ color: C.primary }}>{t(`${I18N}.toolbar.refreshing`)}</span>}
                    </span>
                )}
                <Button size="small" loading={loading} onClick={refresh}>
                    {t(`${I18N}.toolbar.refresh`)}
                </Button>
                <span className="flex-1" />
                <span className="text-[12.5px]" style={{ color: C.text3 }}>
                    {t(`${I18N}.toolbar.hint`)}
                </span>
            </div>
            <p className="mb-3 mt-2 text-black">⏰ {t(`${I18N}.hubLocalTimeNotice`)}</p>

            <Alert
                className="mb-3"
                type="warning"
                showIcon
                message={t(`${I18N}.mockNotice`)}
                description={
                    supported
                        ? undefined
                        : t(`${I18N}.mockHubFallback`, { current: hubFirstToken || '-', hub })
                }
            />

            {error && (
                <Alert
                    className="mb-3"
                    type="error"
                    showIcon
                    message={error === LOAD_FAILED ? t(`${I18N}.loadError`) : error}
                />
            )}

            {!data ? (
                <Skeleton active paragraph={{ rows: 10 }} />
            ) : (
                <>
                    {/* 仓内现状 */}
                    <div className="mb-4 rounded-md bg-white" style={{ border: `1px solid ${C.border}` }}>
                        <div
                            className="flex flex-wrap items-center gap-3 rounded-t-md px-3 py-2.5"
                            style={{ background: C.primaryBg, borderBottom: `1px solid ${C.border}` }}
                        >
                            <div
                                className="text-[17px] font-semibold"
                                style={{ color: C.text1, borderLeft: `3px solid ${C.primary}`, paddingLeft: 8 }}
                            >
                                {t(`${I18N}.current.title`)}
                            </div>
                            <span title={t(`${I18N}.customers.filterTitle`) ?? ''}>
                                <Select<string>
                                    size="small"
                                    value={customer}
                                    onChange={setCustomer}
                                    showSearch
                                    optionFilterProp="label"
                                    style={{ minWidth: 140, maxWidth: 220 }}
                                    popupMatchSelectWidth={false}
                                    options={[
                                        { value: '', label: t(`${I18N}.customers.all`) ?? '' },
                                        ...data.customers.map((c) => ({ value: c, label: L.customer(c) }))
                                    ]}
                                />
                            </span>
                            <span className="text-[12.5px]" style={{ color: C.text3 }}>
                                {t(`${I18N}.current.hint`)}
                            </span>
                            <span className="flex-1" />
                            <div className="text-right">
                                <span className="text-[27px] font-semibold" style={{ color: C.text1 }}>
                                    {fmt.pcs(inTransitParcels(totals))}
                                </span>{' '}
                                <span className="text-[12.5px]" style={{ color: C.text3 }}>
                                    {t(`${I18N}.current.inTransitParcels`)}
                                </span>{' '}
                                <span className="text-[27px] font-semibold" style={{ color: C.text1 }}>
                                    {fmt.num(totalBags(totals))}
                                </span>{' '}
                                <span className="text-[12.5px]" style={{ color: C.text3 }}>
                                    {t(`${I18N}.current.inTransitBags`)}
                                </span>
                            </div>
                        </div>

                        <StageCards
                            totals={totals}
                            alerts={alerts}
                            now={now}
                            overdueThresholdHours={th}
                            focusStage={focusStage}
                            onCardClick={(s) => {
                                // 点卡片：下方流向表按该环节的包裹数从高到低排序（再点一次取消）
                                setFocusStage(focusStage === s ? 0 : s);
                                setSort('total');
                            }}
                            onOpenParcels={(stage, total) => openParcelDrawer('', stage, {}, total)}
                            onOpenBags={(stage, bags) => openBagDrawer('', stage, bags)}
                        />
                        <div
                            className="px-3 pb-2.5 text-[12.5px]"
                            style={{ color: C.text3, borderTop: `1px solid ${C.borderLight}`, paddingTop: 6 }}
                        >
                            {t(`${I18N}.current.footer`)}
                        </div>
                    </div>

                    {/* 流向展开 */}
                    <FlowTable
                        tree={tree}
                        now={now}
                        overdueThresholdHours={th}
                        focusStage={focusStage}
                        sort={sort}
                        onSortChange={setSort}
                        onClearFocus={() => setFocusStage(0)}
                        onOpenParcels={openParcelDrawer}
                        onOpenBags={openBagDrawer}
                        onOpenRowBags={openRowBagDrawer}
                    />

                    {/* 仓内作业 */}
                    <OpsSection
                        hub={hub}
                        timeZone={timeZone}
                        customer={customer}
                        now={now}
                        opsHours={data.opsHours}
                        opsAccounts={data.opsAccounts}
                        sorterHours={data.sorterHours}
                        sorterDevices={data.sorterDevices}
                    />
                </>
            )}

            <DetailDrawerLazy
                drawer={drawer}
                now={now}
                customers={data?.customers ?? []}
                onClose={() => {
                    // 作废还没回来的明细查询：关抽屉前输入框失焦会触发一次重查，回来后不能把抽屉又打开
                    drawerToken.current += 1;
                    setDrawer(null);
                }}
                onParcelFilter={onParcelFilter}
                onExportParcels={exportDetails}
            />
        </div>
    );
};

/** 抽屉只在打开过一次之后才挂载，避免首屏多加载一个 chunk */
const DetailDrawer = React.lazy(() => import('./components/DetailDrawer'));

const DetailDrawerLazy: React.FC<DetailDrawerProps> = (props) => {
    const [mounted, setMounted] = React.useState(false);
    React.useEffect(() => {
        if (props.drawer) setMounted(true);
    }, [props.drawer]);
    if (!mounted) return null;
    return (
        <React.Suspense fallback={null}>
            <DetailDrawer {...props} />
        </React.Suspense>
    );
};

export default BagStayMonitorPage;
